import { formatDate, NgIf, NgFor, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, LOCALE_ID, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AnalyticsService, APP_SETTINGS, SpinnerService, ToastConfigService } from '@services';
import { ValidationUtils } from 'src/app/shared/utils/validation.utils';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { IMaskDirective } from 'angular-imask';

@Component({
    selector: 'app-manage-user',
    templateUrl: './manage-user.component.html',
    styleUrls: ['./manage-user.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        NgIf,
        IMaskDirective,
        NgFor,
        NgStyle,
        RouterLink,
    ],
})
export class ManageUserComponent implements OnInit {

  private readonly locale = inject(LOCALE_ID);

  public firstName = new UntypedFormControl('');
  public lastName = new UntypedFormControl('');
  public expirationDate = new UntypedFormControl('');
  public address1 = new UntypedFormControl('');
  public address2 = new UntypedFormControl('');
  public city = new UntypedFormControl('');
  public state = new UntypedFormControl('');
  public zipCode = new UntypedFormControl('');
  public phone = new UntypedFormControl('');
  public email = new UntypedFormControl('', [Validators.required, Validators.pattern(ValidationUtils.emailPattern)]);
  public reEnterEmail = new UntypedFormControl('', [Validators.required, Validators.pattern(ValidationUtils.emailPattern)]);
  public qID = new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(ValidationUtils.qIDPattern)]));
  public inputLinemake = new UntypedFormControl('');
  public userType = {};
  public user = {};
  public userForm: UntypedFormGroup;
  public errorMessage = '';
  public displayQID: string;

  APP_SETTINGS: any;

  action: string;

  public linemakes = ['BMW', 'MINI', 'ROLLSROYCE'];

  public phoneMask = { mask: '(000) 000-0000' };

  constructor(private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private userProfileService: UserProfileService,
    private spinnerService: SpinnerService,
    private toastService: ToastConfigService,
    private analyticsService: AnalyticsService
  ) { }

  ngOnInit() {
    this.APP_SETTINGS = APP_SETTINGS;
    this.userForm = new UntypedFormGroup({});
    this.action = this.route.snapshot.params['action'];

    if (this.action === 'edit') {
      this.spinnerService.show('bmwSpinner');
      this.route.queryParams.subscribe(params => {

        this.user = params;
        this.displayQID = params.qID;
        this.firstName = new UntypedFormControl(params.firstName);
        this.lastName = new UntypedFormControl(params.lastName);
        this.expirationDate = new UntypedFormControl(
          params.accountExpiryDate ? formatDate(params.accountExpiryDate, "yyyy-MM-dd'T'HH:mm", this.locale) : ''
        );
        this.userType = {}

        this.userType['id'] = params.userTypeId;
        this.userType['userTypeName'] = params.userTypeName;
        this.userType['userTypeDesc'] = params.userTypeDesc;

        this.qID = new UntypedFormControl(params.qID);
        this.address1 = new UntypedFormControl(params.address1);
        this.address2 = new UntypedFormControl(params.address2);
        this.city = new UntypedFormControl(params.city);
        this.state = new UntypedFormControl(params.state);
        this.phone = new UntypedFormControl(params.phone);
        this.zipCode = new UntypedFormControl(params.zipCode);
        this.email = new UntypedFormControl(params.email);
        this.reEnterEmail = new UntypedFormControl(params.email);
        this.inputLinemake = new UntypedFormControl(params.brandType);
      });
      this.spinnerService.hide('bmwSpinner');
    }
    this.userForm = this.fb.group({
      firstName: this.firstName,
      lastName: this.lastName,
      expirationDate: this.expirationDate,
      address1: this.address1,
      address2: this.address2,
      qID: this.qID,
      city: this.city,
      state: this.state,
      phone: this.phone,
      zipCode: this.zipCode,
      email: this.email,
      reEnterEmail: this.reEnterEmail,
      inputLinemake: this.inputLinemake
    });

  }
  onFormSubmit() {
    let userObj = {};
    if (this.userForm.controls.email.value != this.userForm.controls.reEnterEmail.value) {
      return alert("Email and Re-Enter Email both are not matching..");
    }
    if (this.action === 'add') {
      const expirationDateValue = this.userForm.value.expirationDate;

      if ('MINI' == this.inputLinemake.value) {
        userObj = { ...this.userForm.value, 'isBMW': 'false', 'isMINI': 'true', 'isROLLSROYCE': 'false', 'accountExpiryDate': expirationDateValue, 'passwordExpiryDate': expirationDateValue };
      } else if ('ROLLSROYCE' == this.inputLinemake.value) {
        userObj = { ...this.userForm.value, 'isBMW': 'false', 'isMINI': 'false', 'isROLLSROYCE': 'true', 'accountExpiryDate': expirationDateValue, 'passwordExpiryDate': expirationDateValue };
      } else {
        userObj = { ...this.userForm.value, 'isBMW': 'true', 'isMINI': 'false', 'isROLLSROYCE': 'false', 'accountExpiryDate': expirationDateValue, 'passwordExpiryDate': expirationDateValue };
      }
      this.userProfileService.saveNewUser(userObj).subscribe(responseData => {
        this.errorMessage = '';
        this.toastService.popToast('User Added');
        this.userForm.reset();
        this.user = {};
      },
        error => {
          this.errorMessage = error.error.message;
        });
    } else {
      const accountExpiration = this.userForm.value.expirationDate ? new Date(this.userForm.value.expirationDate) : null;

      userObj = { ...this.user, ...this.userForm.value, 'accountExpiryDate': accountExpiration };
      this.userProfileService.updateUserInfo(userObj).subscribe(responseData => {
        this.errorMessage = '';
        this.toastService.popToast('User Updated');
        this.userForm.reset();
        this.displayQID = '';
        this.user = {};
      },
        error => {
          this.errorMessage = error.error.message;
        });
    }
    this.analyticCallAddUpdateUser(this.userForm.controls.qID.value, this.userForm.controls.address1.value, this.userForm.controls.city.value,
      this.userForm.controls.state.value, this.userForm.controls.inputLinemake.value);
  }

  analyticCallAddUpdateUser(userId: string, Address1: string, city: string, state: string, brandName: string) {
    if (this.action === 'add') {
      this.analyticsService.trackNewThirdPartyUserAdded(userId, Address1, city, state, brandName)
    }
    else if (this.action === 'edit') {
      this.analyticsService.trackNewThirdPartyUserUpdated(userId, Address1, city, state, brandName)
    }
  }
}
